import { EngagementClock } from "./engagement-clock";
import { navigationKey } from "./policy";

export type ViewPayload = {
  eventId: string;
  url: string;
  referrer: string | null;
  navigation: "arrival" | "internal" | "reload" | "history";
};
export type TrackedView = {
  payload: ViewPayload;
  clock: EngagementClock;
  receipt: string | null;
  closed: boolean;
  attempts: number;
  sentMs: number;
  started: boolean;
};
type Environment = {
  now: () => number;
  allowed: () => boolean;
  foreground: () => boolean;
  visit: (payload: ViewPayload) => Promise<string | null>;
  engagement: (
    eventId: string,
    receipt: string,
    activeMs: number,
  ) => Promise<void>;
};

export function entityMatchesPage(entity: string, page: string) {
  const target = new URL(entity, "https://farbin.co");
  const current = new URL(page, "https://farbin.co");
  if (target.pathname === current.pathname) return true;
  const slug = target.pathname.match(/^\/(?:en|fa)\/c\/(.+)$/)?.[1];
  if (!slug || !/^\/(?:en|fa)\/newsroom\/?$/.test(current.pathname))
    return false;
  try {
    return decodeURIComponent(slug) === current.searchParams.get("focus");
  } catch {
    return false;
  }
}

// All visible surfaces share this registry. A route-backed newsroom viewer aliases
// its page event; independent entity openings get their own view and timer.
export class ViewCoordinator {
  private page: TrackedView | null = null;
  private pageKey: string | null = null;
  private pageLease = 0;
  private overlays = new Map<
    object,
    { view: TrackedView; active: boolean; owned: boolean; key: string }
  >();
  constructor(private env: Environment) {}
  private create(
    url: string,
    navigation: ViewPayload["navigation"],
    referrer: string | null,
  ): TrackedView {
    return {
      payload: { eventId: crypto.randomUUID(), url, referrer, navigation },
      clock: new EngagementClock(this.env.now()),
      receipt: null,
      closed: false,
      attempts: 0,
      sentMs: 0,
      started: false,
    };
  }
  private top() {
    return (
      [...this.overlays.values()]
        .filter((entry) => entry.active && !entry.view.closed)
        .at(-1)?.view || this.page
    );
  }
  private views() {
    return new Set([
      ...(this.page ? [this.page] : []),
      ...[...this.overlays.values()].map((entry) => entry.view),
    ]);
  }
  openPage(
    url: string,
    navigation: ViewPayload["navigation"],
    referrer: string | null,
    force = false,
  ) {
    const key = navigationKey(new URL(url, "https://farbin.co").href);
    if (force || this.pageKey !== key || !this.page || this.page.closed) {
      if (this.page) this.finish(this.page);
      this.page = this.create(url, navigation, referrer);
      this.pageKey = key;
    }
    const lease = ++this.pageLease;
    this.sync();
    return () =>
      queueMicrotask(() => {
        if (lease === this.pageLease && this.page) {
          this.finish(this.page);
          this.page = null;
          this.pageKey = null;
          this.sync();
        }
      });
  }
  openEntity(owner: object, key: string, url: string) {
    const previous = this.overlays.get(owner);
    if (previous && previous.key === key && !previous.view.closed)
      previous.active = true;
    else {
      if (previous?.owned) this.finish(previous.view);
      const alias =
        this.page &&
        !this.page.closed &&
        entityMatchesPage(url, this.page.payload.url);
      this.overlays.set(owner, {
        view: alias ? this.page! : this.create(url, "internal", null),
        active: true,
        owned: !alias,
        key,
      });
    }
    this.sync();
    return () => {
      const entry = this.overlays.get(owner);
      if (!entry) return;
      entry.active = false;
      queueMicrotask(() => {
        if (this.overlays.get(owner) === entry && !entry.active) {
          if (entry.owned) this.finish(entry.view);
          this.overlays.delete(owner);
          this.sync();
        }
      });
    };
  }
  private submit(view: TrackedView) {
    if (view.started || view.attempts >= 2 || !this.env.allowed()) return;
    view.started = true;
    view.attempts++;
    void this.env
      .visit(view.payload)
      .then((receipt) => {
        view.receipt = receipt;
        if (view.closed) this.checkpoint(view);
      })
      .catch(() => {
        view.started = false;
      });
  }
  sync() {
    const top = this.top();
    const allowed = this.env.allowed();
    const now = this.env.now();
    for (const view of this.views())
      view.clock.setRunning(
        now,
        allowed && this.env.foreground() && view === top && !view.closed,
      );
    if (top && !top.closed && allowed) this.submit(top);
  }
  activity() {
    const top = this.top();
    if (top && this.env.allowed() && this.env.foreground())
      top.clock.activity(this.env.now());
  }
  checkpoint(view: TrackedView) {
    const activeMs = view.clock.snapshot(this.env.now());
    if (!view.receipt || activeMs <= view.sentMs || !this.env.allowed()) return;
    void this.env
      .engagement(view.payload.eventId, view.receipt, activeMs)
      .then(() => {
        view.sentMs = Math.max(view.sentMs, activeMs);
      })
      .catch(() => undefined);
  }
  flush() {
    this.sync();
    for (const view of this.views()) this.checkpoint(view);
  }
  suspend() {
    for (const view of this.views()) {
      view.clock.setRunning(this.env.now(), false);
      this.checkpoint(view);
    }
  }
  private finish(view: TrackedView) {
    if (view.closed) return;
    view.clock.setRunning(this.env.now(), false);
    view.closed = true;
    this.checkpoint(view);
  }
}
